import {
  CanActivate,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  Router,
  UrlTree
} from "@angular/router";
import { Injectable } from "@angular/core";
import { firstValueFrom } from "rxjs";

import { AuthService } from "./auth.service";

@Injectable()
export class AuthGuard implements CanActivate {
  constructor(private authService: AuthService) {}

  async canActivate(
    _route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Promise<boolean | UrlTree> {
    return this.authService.guardAuthenticatedWithCompleteProfile(state.url);
  }
}

/** Redirects unauthenticated users to signup. Use for registration flows. */
@Injectable({ providedIn: 'root' })
export class RegistrationAuthGuard implements CanActivate {
  constructor(private authService: AuthService) {}

  async canActivate(
    _route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Promise<boolean | UrlTree> {
    return this.authService.guardRegistrationWithCompleteProfile(state.url);
  }
}

@Injectable({ providedIn: 'root' })
export class NoAuthGuard implements CanActivate {
  constructor(private authService: AuthService, private router: Router) {}

  async canActivate(): Promise<boolean | UrlTree> {
    const userInfo = await firstValueFrom(this.authService.getAuthStatusListener());
    if (!userInfo.isAuthenticated) {
      return true;
    }
    try {
      const profile = await firstValueFrom(this.authService.getProfile());
      if (!this.authService.isProfileComplete(profile)) {
        return this.router.createUrlTree(['/auth/complete-profile']);
      }
    } catch {
      return this.router.createUrlTree(['/auth/login']);
    }
    return this.router.createUrlTree(['/']);
  }
}

/** Authenticated users with incomplete signup finish profile here. */
@Injectable({ providedIn: 'root' })
export class CompleteProfileGuard implements CanActivate {
  constructor(private authService: AuthService, private router: Router) {}

  async canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Promise<boolean | UrlTree> {
    const userInfo = await firstValueFrom(this.authService.getAuthStatusListener());
    if (!userInfo.isAuthenticated) {
      return this.router.createUrlTree(['/auth/login'], {
        queryParams: { returnUrl: state.url },
      });
    }
    try {
      const profile = await firstValueFrom(this.authService.getProfile());
      if (this.authService.isProfileComplete(profile)) {
        const returnUrl = route.queryParamMap.get('returnUrl');
        return this.router.createUrlTree([
          this.authService.resolveReturnUrl(returnUrl),
        ]);
      }
      return true;
    } catch {
      return this.router.createUrlTree(['/auth/login'], {
        queryParams: { returnUrl: state.url },
      });
    }
  }
}
